import LandingNavbar from "@/components/landing/LandingNavbar";
import LandingFooter from "@/components/landing/LandingFooter";

const Privacy = () => {
  return (
    <div className="min-h-screen bg-background">
      <LandingNavbar />
      
      <main className="pt-24 pb-16 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="prose prose-lg max-w-none">
            <h1 className="text-4xl font-bold text-foreground mb-2 font-display">Privacy Policy</h1>
            <p className="text-muted-foreground mb-8">Last Updated November 16, 2025</p>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-foreground mt-10 mb-4 font-display">About StoryPlay Privacy</h2>
              <p className="text-foreground/90 leading-relaxed mb-4">
                StoryPlay is committed to protecting the privacy of its users. This Privacy Policy describes how StoryPlay (referred to as "StoryPlay," "we," "us," or "our") collects, uses, and discloses information about you when you visit and interact with our web application located at www.storyplay.in or its subdomains (collectively, the "Websites"), the StoryPlay mobile application available on Android devices ("App") (collectively, "StoryPlay," "Platform," or "Service").
              </p>
              <p className="text-foreground/90 leading-relaxed mb-4">
                By using, accessing, or interacting with StoryPlay:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-foreground/90">
                <li>You agree to be bound by this Privacy Policy.</li>
                <li>You consent to the collection, use, and sharing of your data as stated in this Policy and to the Terms of Service.</li>
                <li>You confirm that you are of legal adult age in your jurisdiction. If you are under 18, you must have parental or legal guardian consent.</li>
                <li>If you do not agree to these Terms, you must stop using StoryPlay.</li>
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-foreground mt-10 mb-4 font-display">Your Consent to Our Privacy Practices</h2>
              <p className="text-foreground/90 leading-relaxed mb-4">
                By using StoryPlay, you consent to the privacy practices described in this Privacy Policy. This includes how we collect, use, and disclose your information, and any additional terms specific to your subscription or usage plan.
              </p>
              <p className="text-foreground/90 leading-relaxed">
                If we need to use your personal information for purposes not covered by this Privacy Policy, we will obtain your consent first, except where required or permitted by law.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-foreground mt-10 mb-4 font-display">Notice to Schools</h2>
              <p className="text-foreground/90 leading-relaxed">
                If StoryPlay is accessed through an educational institution, the school may be responsible for obtaining the necessary consent from parents or guardians. Schools are advised to ensure compliance with applicable educational privacy laws.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-foreground mt-10 mb-4 font-display">Privacy & Personal Information</h2>
              <p className="text-foreground/90 leading-relaxed mb-4">
                Personal information refers to data that can identify you directly or indirectly. Categories of personal information we may collect include:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-foreground/90">
                <li>Identifiers: name, email address, username, phone number</li>
                <li>Commercial Information: subscription details, purchase history</li>
                <li>Usage Data: interaction patterns, content preferences</li>
                <li>Device Information: device type, browser type, IP address</li>
                <li>Child's Information (with parental consent): name, age, interests (used to personalize stories)</li>
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-foreground mt-10 mb-4 font-display">Keeping Children Safe Online</h2>
              <p className="text-foreground/90 leading-relaxed">
                StoryPlay is committed to children's safety. All content is designed to be age-appropriate. We do not knowingly collect personal information from children under 13 without verifiable parental consent. Parents or guardians control all account settings and data sharing preferences. We recommend adult supervision for all children using the platform.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-foreground mt-10 mb-4 font-display">Personal Information Collection and Use on StoryPlay</h2>
              
              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">Account Creators</h3>
              <p className="text-foreground/90 leading-relaxed mb-4">
                When you create an account, we collect your name, email address, and password. This information is used to provide access to StoryPlay services and manage your subscription.
              </p>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">Gifted StoryPlay Subscriptions</h3>
              <p className="text-foreground/90 leading-relaxed mb-4">
                If you receive a gifted subscription, the gift giver may provide your email address. We use this solely to set up your account.
              </p>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">Your School's LMS</h3>
              <p className="text-foreground/90 leading-relaxed mb-4">
                If StoryPlay is integrated with a school's Learning Management System, certain information may be shared between systems in accordance with educational privacy agreements.
              </p>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">Content Interactions</h3>
              <p className="text-foreground/90 leading-relaxed mb-4">
                We collect data on your interactions with stories, including listening history and preferences, to improve and personalize content recommendations.
              </p>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">Your Messages to StoryPlay</h3>
              <p className="text-foreground/90 leading-relaxed mb-4">
                If you contact us via email or support channels, we collect the content of your messages to respond and improve our services.
              </p>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">Your Subscription Payment</h3>
              <p className="text-foreground/90 leading-relaxed">
                Payment transactions are processed securely through Razorpay. We do not store your credit card details on our servers.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-foreground mt-10 mb-4 font-display">Retention of Personal Information</h2>
              <p className="text-foreground/90 leading-relaxed">
                We retain personal information for as long as necessary to provide services, comply with legal obligations, resolve disputes, and enforce agreements. You may request deletion of your data at any time, subject to legal requirements.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-foreground mt-10 mb-4 font-display">Disclosures to Third Parties</h2>
              <p className="text-foreground/90 leading-relaxed mb-4">
                We may share your information with:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-foreground/90">
                <li>Service Providers: to process payments, host data, and provide customer support</li>
                <li>Legal Requirements: to comply with legal processes, enforce policies, or protect rights</li>
                <li>Business Transfers: in the event of a merger, acquisition, or sale of assets</li>
              </ul>
              <p className="text-foreground/90 leading-relaxed mt-4">
                We do not sell personal information to third parties for marketing purposes.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-foreground mt-10 mb-4 font-display">StoryPlay & Cookies</h2>
              <p className="text-foreground/90 leading-relaxed mb-4">
                StoryPlay uses cookies and similar technologies to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-foreground/90">
                <li>Essential Cookies: enable core functionality and security</li>
                <li>Analytics Cookies: understand how users interact with the platform</li>
                <li>Preference Cookies: remember your settings and preferences</li>
              </ul>
              <p className="text-foreground/90 leading-relaxed mt-4">
                You can manage cookie preferences through your browser settings.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-foreground mt-10 mb-4 font-display">Your Privacy Controls</h2>
              <p className="text-foreground/90 leading-relaxed mb-4">
                You have the right to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-foreground/90">
                <li>Access your personal information</li>
                <li>Update or correct your data</li>
                <li>Delete your account and associated data</li>
                <li>Opt-out of marketing communications</li>
                <li>Manage cookie preferences</li>
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-foreground mt-10 mb-4 font-display">Block Location Tracking</h2>
              <p className="text-foreground/90 leading-relaxed">
                StoryPlay does not track your precise location. We may use general location data (e.g., country) based on your IP address to improve services.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-foreground mt-10 mb-4 font-display">Texting</h2>
              <p className="text-foreground/90 leading-relaxed">
                We may send SMS messages for account verification or important updates if you provide your phone number. You can opt-out of marketing SMS at any time.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-foreground mt-10 mb-4 font-display">Do Not Track</h2>
              <p className="text-foreground/90 leading-relaxed">
                StoryPlay currently does not respond to Do Not Track browser signals. We continue to monitor industry standards and may update our practices accordingly.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-foreground mt-10 mb-4 font-display">Students' Parent's Rights</h2>
              <p className="text-foreground/90 leading-relaxed">
                Parents or guardians of students using StoryPlay through schools have the right to review, request deletion, and manage their child's data. Contact your school or StoryPlay directly to exercise these rights.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-foreground mt-10 mb-4 font-display">Privacy Requests</h2>
              <p className="text-foreground/90 leading-relaxed">
                To make a privacy request, contact us at{" "}
                <a href="mailto:support@storyplay.in" className="text-[hsl(var(--playful-pink))] hover:underline">
                  support@storyplay.in
                </a>
                . We will respond within 30 days.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-foreground mt-10 mb-4 font-display">Notice of Privacy Rights</h2>
              <p className="text-foreground/90 leading-relaxed">
                Depending on your location, you may have specific privacy rights under applicable laws. StoryPlay is committed to honoring these rights.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-foreground mt-10 mb-4 font-display">Privacy Rights</h2>
              
              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">Right to Know</h3>
              <p className="text-foreground/90 leading-relaxed mb-4">
                You have the right to know what personal information we collect and how it is used.
              </p>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">No Selling/Sharing</h3>
              <p className="text-foreground/90 leading-relaxed mb-4">
                We do not sell or share your personal information with third parties for their direct marketing purposes.
              </p>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">Limited Use</h3>
              <p className="text-foreground/90 leading-relaxed mb-4">
                Your personal information is used only for the purposes stated in this Privacy Policy.
              </p>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">Right to Expect</h3>
              <p className="text-foreground/90 leading-relaxed mb-4">
                You can expect us to handle your personal information with care and in accordance with this Privacy Policy.
              </p>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">Right to Accuracy</h3>
              <p className="text-foreground/90 leading-relaxed mb-4">
                You have the right to request correction of inaccurate personal information.
              </p>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">Right to Access</h3>
              <p className="text-foreground/90 leading-relaxed mb-4">
                You have the right to access your personal information held by StoryPlay.
              </p>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">No Automated Decision-Making</h3>
              <p className="text-foreground/90 leading-relaxed mb-4">
                StoryPlay does not use automated decision-making that significantly affects you without human oversight.
              </p>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">No Health Data Collected</h3>
              <p className="text-foreground/90 leading-relaxed mb-4">
                We do not collect health-related personal information.
              </p>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">Right to Data Portability</h3>
              <p className="text-foreground/90 leading-relaxed mb-4">
                You have the right to receive your personal information in a structured, commonly used format.
              </p>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">Right to Delete</h3>
              <p className="text-foreground/90 leading-relaxed mb-4">
                You have the right to request deletion of your personal information, subject to legal requirements.
              </p>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">Right to Object</h3>
              <p className="text-foreground/90 leading-relaxed mb-4">
                You have the right to object to certain processing of your personal information.
              </p>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">Right to Restrict Processing</h3>
              <p className="text-foreground/90 leading-relaxed mb-4">
                You have the right to request restriction of processing your personal information in certain circumstances.
              </p>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">Right to Nondiscrimination</h3>
              <p className="text-foreground/90 leading-relaxed mb-4">
                You have the right not to be discriminated against for exercising your privacy rights.
              </p>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">Right to Disclosure</h3>
              <p className="text-foreground/90 leading-relaxed mb-4">
                You have the right to request disclosure of what personal information we collect and share.
              </p>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">Right to Disclosure of Marketing Information</h3>
              <p className="text-foreground/90 leading-relaxed">
                You have the right to know if your personal information is used for marketing purposes and to opt-out.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-foreground mt-10 mb-4 font-display">Consent to Cross-Border Data Transfers</h2>
              <p className="text-foreground/90 leading-relaxed">
                Your personal information may be transferred to and processed in countries other than your own. By using StoryPlay, you consent to such transfers, which are conducted in compliance with applicable data protection laws.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-foreground mt-10 mb-4 font-display">StoryPlay Data Security</h2>
              <p className="text-foreground/90 leading-relaxed">
                We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet is 100% secure.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-foreground mt-10 mb-4 font-display">Third-Party Services</h2>
              <p className="text-foreground/90 leading-relaxed">
                StoryPlay may contain links to third-party services. We are not responsible for the privacy practices of these services. We encourage you to review their privacy policies.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-foreground mt-10 mb-4 font-display">Updates</h2>
              <p className="text-foreground/90 leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of significant changes through the platform or via email. Continued use of StoryPlay after changes constitutes acceptance of the updated Privacy Policy.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-foreground mt-10 mb-4 font-display">Contact StoryPlay</h2>
              <p className="text-foreground/90 leading-relaxed">
                If you have any questions or concerns about this Privacy Policy, please contact us at{" "}
                <a href="mailto:support@storyplay.in" className="text-[hsl(var(--playful-pink))] hover:underline">
                  support@storyplay.in
                </a>
                .
              </p>
            </section>
          </div>
        </div>
      </main>

      <LandingFooter />
    </div>
  );
};

export default Privacy;
